/**
 * ResolutionHistoryModal - Displays paginated resolution history for a message thread.
 *
 * Fetches from /messages-v2/resolution-history with server-side pagination.
 * Pagination uses the same custom paginatorTemplate pattern as CaseListV2.
 */

import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Skeleton } from 'primereact/skeleton';

import { Link } from 'react-router-dom';

import constants from 'constants/index';
import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import { paginationDropdownOptions } from 'utils/utils';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { fetchResolutionHistory } from '../../services/messageService';
import { generateFullName } from 'utils/generateFullNameUtils';

const VISIBLE_COUNT = 2;

// ── Helpers ───────────────────────────────────────────────────────────────────

function UserLink({ user }) {
  if (!user) return <span>—</span>;
  const name = generateFullName(user);
  if (!user.firm_user_id) return <span>{name}</span>;
  return (
    <Link
      to={`/settings/team-management/${user.firm_user_id}`}
      className="F-size14 text-primary-dark text-decoration-none case-details-link pointer"
    >
      {name}
    </Link>
  );
}

/** Renders up to VISIBLE_COUNT names inline with links, with a +X more badge + OverlayPanel for the rest. */
function AssignedUsersCell({ users }) {
  const opRef = useRef(null);
  const visible = users.slice(0, VISIBLE_COUNT);
  const overflow = users.slice(VISIBLE_COUNT);

  if (!users.length) return <span>—</span>;

  return (
    <div className="d-flex align-items-center flex-wrap gap-1">
      <span>
        {visible.map((u, i) => {
          const withComma = i < visible.length - 1 || overflow.length > 0;
          return (
            <span key={u.firm_user_id || i}>
              <UserLink user={u} />
              {withComma ? ', ' : ''}
            </span>
          );
        })}
      </span>

      {overflow.length > 0 && (
        <>
          <span
            className="participant-overflow-badge"
            role="button"
            tabIndex={0}
            onClick={(e) => opRef.current?.toggle(e)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                opRef.current?.toggle(e);
              }
            }}
            aria-label={`Show ${overflow.length} more assigned user${overflow.length > 1 ? 's' : ''}`}
          >
            +{overflow.length} more
          </span>

          <OverlayPanel ref={opRef} dismissable className="participants-overflow-panel">
            <ul className="participants-overflow-panel__list">
              {overflow.map((u, i) => (
                <li key={u.firm_user_id || i} className="participants-overflow-panel__item">
                  <UserLink user={u} />
                </li>
              ))}
            </ul>
          </OverlayPanel>
        </>
      )}
    </div>
  );
}

AssignedUsersCell.propTypes = {
  users: PropTypes.arrayOf(PropTypes.object).isRequired,
};

// ── Skeleton loader ───────────────────────────────────────────────────────────

const COL_WIDTHS = ['160px', '150px', '180px', '130px', '150px', '160px'];
const COL_HEADERS = ['Requested On', 'Requested By', 'Assigned Users', 'Current Status', 'Resolved By', 'Resolved On'];

function ResolutionHistorySkeleton({ rows = 5 }) {
  return (
    <table className="p-datatable-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead className="p-datatable-thead">
        <tr>
          {COL_HEADERS.map((h) => (
            <th
              key={h}
              style={{
                minWidth: COL_WIDTHS[COL_HEADERS.indexOf(h)],
                padding: '0.75rem 1rem',
                textAlign: 'left',
                borderBottom: '1px solid var(--surface-d, #e9ecef)',
              }}
            >
              <span className="visually-hidden">{h}</span>
              <Skeleton height="14px" width="80%" />
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="p-datatable-tbody">
        {Array.from({ length: rows }).map((_, ri) => (
          <tr key={ri} style={{ borderBottom: '1px solid var(--surface-d, #e9ecef)' }}>
            {COL_WIDTHS.map((w, ci) => (
              <td key={ci} style={{ minWidth: w, padding: '0.75rem 1rem' }}>
                <Skeleton height="14px" width={ci === 2 ? '90%' : '70%'} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Component ────────────────────────────────────────────────────────────────

function ResolutionHistoryModal({ visible, onHide, messageThreadId }) {
  const { userDetails } = useUserDetailsContext();
  const timezone = userDetails?.timezone;

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const loadPage = useCallback(
    (pageNum, size) => {
      if (!messageThreadId) return;
      setLoading(true);
      fetchResolutionHistory({ message_thread_id: messageThreadId, page: pageNum, page_size: size })
        .then((res) => {
          const body = res?.data?.body ?? res?.data ?? {};
          setItems(body.items || []);
          setTotalPages(body.pagination?.total_pages || 1);
        })
        .catch(() => {
          setItems([]);
          setTotalPages(1);
        })
        .finally(() => setLoading(false));
    },
    [messageThreadId]
  );

  useEffect(() => {
    if (visible) loadPage(page, pageSize);
  }, [visible, page, pageSize, loadPage]);

  const fetchNext = useCallback(() => {
    setPage((p) => p + 1);
  }, []);

  const fetchPrev = useCallback(() => {
    setPage((p) => p - 1);
  }, []);

  const onPageSizeChange = useCallback((e) => {
    setPageSize(e.value);
    setPage(1);
  }, []);

  const handleHide = useCallback(() => {
    setPage(1);
    setPageSize(10);
    setItems([]);
    setTotalPages(1);
    onHide();
  }, [onHide]);

  // ── Paginator template (matches CaseListV2 pattern) ───────────────────────

  const paginatorTemplate = {
    layout: 'RowsPerPageDropdown PrevPageLink NextPageLink',
    RowsPerPageDropdown: () => (
      <React.Fragment>
        <span className="mx-1" style={{ userSelect: 'none' }}>
          Items Per Page
        </span>
        <Dropdown value={pageSize} options={paginationDropdownOptions} onChange={onPageSizeChange} />
      </React.Fragment>
    ),
    PrevPageLink: ({ iconClassName }) => (
      <Button onClick={fetchPrev} disabled={page <= 1} className="p-paginator-prev p-paginator-element p-link" icon={iconClassName} />
    ),
    NextPageLink: ({ iconClassName }) => (
      <Button
        onClick={fetchNext}
        disabled={page >= totalPages}
        className="p-paginator-next p-paginator-element p-link"
        icon={iconClassName}
      />
    ),
  };

  // ── Column templates ───────────────────────────────────────────────────────

  const fmtDate = useCallback(
    (dateStr) => (dateStr ? convertDateFromApiToUserTimezone(dateStr, timezone, constants.month_date_year_time_12_format) || '—' : '—'),
    [timezone]
  );

  const requestedOnBody = (row) => (
    <React.Fragment>
      <span className="p-column-title text-break">Requested On</span>
      <span>{fmtDate(row.requested_on)}</span>
    </React.Fragment>
  );
  const requestedByBody = (row) => (
    <React.Fragment>
      <span className="p-column-title text-break">Requested By</span>
      <UserLink user={row.requested_by} />
    </React.Fragment>
  );
  const assignedUsersBody = (row) => (
    <React.Fragment>
      <span className="p-column-title text-break">Assigned Users</span>
      <AssignedUsersCell users={row.assigned_users || []} />
    </React.Fragment>
  );
  const statusBody = (row) => (
    <React.Fragment>
      <span className="p-column-title text-break">Current Status</span>
      <span>{row.status_display || row.status || '—'}</span>
    </React.Fragment>
  );
  const resolvedByBody = (row) => (
    <React.Fragment>
      <span className="p-column-title text-break">Resolved By</span>
      <UserLink user={row.resolved_by} />
    </React.Fragment>
  );
  const resolvedOnBody = (row) => (
    <React.Fragment>
      <span className="p-column-title text-break">Resolved On</span>
      <span>{fmtDate(row.resolved_on)}</span>
    </React.Fragment>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Dialog
      header="Resolution History"
      visible={visible}
      onHide={handleHide}
      style={{ width: '85vw', maxWidth: '1100px' }}
      className="common-popup-style datatable-responsive"
      modal
    >
      {loading ? (
        <div className="p-datatable p-component p-datatable-sm p-datatable-scrollable" style={{ overflowX: 'auto' }}>
          <ResolutionHistorySkeleton rows={pageSize} />
        </div>
      ) : (
        <DataTable
          value={items}
          paginator
          paginatorTemplate={paginatorTemplate}
          rows={pageSize}
          emptyMessage="No resolution history found."
          className="p-datatable-sm p-datatable-responsive"
          responsiveLayout="scroll"
          rowHover
        >
          <Column header="Requested On" body={requestedOnBody} style={{ minWidth: '160px' }} />
          <Column header="Requested By" body={requestedByBody} />
          <Column header="Assigned Users" body={assignedUsersBody} />
          <Column header="Current Status" body={statusBody} />
          <Column header="Resolved By" body={resolvedByBody} />
          <Column header="Resolved On" body={resolvedOnBody} style={{ minWidth: '160px' }} />
        </DataTable>
      )}
    </Dialog>
  );
}

ResolutionHistoryModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  messageThreadId: PropTypes.string,
};

ResolutionHistoryModal.defaultProps = {
  messageThreadId: null,
};

export default memo(ResolutionHistoryModal);
